import { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  runDatabaseMigrations,
  UserRole,
  getRolePermissions,
  type AdminUser,
  AuditLogger
} from '../utils/databaseMigration';

const allowedAdmins = ['masterfem@gmail.com'];

export const useAuthPermissions = () => {
  const [user, loading] = useAuthState(auth);

  // Role-based access state
  const [currentUserRole, setCurrentUserRole] = useState<UserRole>(UserRole.VIEWER);
  const [userPermissions, setUserPermissions] = useState(getRolePermissions(UserRole.VIEWER));
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  // Force clean state on version change (development helper)
  useEffect(() => {
    const currentVersion = '2.0.0';
    const storedVersion = localStorage.getItem('dashboardVersion');

    if (storedVersion !== currentVersion) {
      console.log('🧹 New version detected, cleaning cached data...');

      // Clear all localStorage data
      const keysToKeep = ['firebase:authUser:[DEFAULT]', 'firebase:host:localhost:3000'];
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !keysToKeep.some(keepKey => key.includes(keepKey))) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      localStorage.setItem('dashboardVersion', currentVersion);

      console.log('✅ Cache cleared, will reinitialize...');
    }
  }, []);

  // Initialize database migrations and load user role
  useEffect(() => {
    const initializeDatabase = async () => {
      if (user) {
        // Set timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          console.error('⏰ Initialization timeout - forcing completion');
          setInitializationError('Initialization took too long. Some features may not work properly.');
          setMigrationComplete(true);
        }, 10000); // 10 second timeout

        try {
          console.log('🔄 Starting database initialization for:', user.email);
          console.log('🔄 User UID:', user.uid);
          console.log('🔄 Current migrationComplete state:', migrationComplete);

          // Run migrations first
          await runDatabaseMigrations();
          console.log('✅ Migrations completed');

          clearTimeout(timeoutId);
          setMigrationComplete(true);

          // Load current user's role
          console.log('🔄 Loading user role for:', user.email);
          console.log('🔍 User UID:', user.uid);
          console.log('🔍 Current allowedAdmins list:', allowedAdmins);
          console.log('🔍 UserRole.SUPER_ADMIN value:', UserRole.SUPER_ADMIN);
          console.log('🔍 UserRole.VIEWER value:', UserRole.VIEWER);

          const userDoc = await getDoc(doc(db, 'usuariosAutorizados', user.email || ''));
          console.log('🔍 Firestore document path:', `usuariosAutorizados/${user.email}`);
          console.log('🔍 Document exists?', userDoc.exists());

          if (userDoc.exists()) {
            const userData = userDoc.data() as AdminUser;
            console.log('✅ User found in admin system:', userData);
            console.log('🔍 User role from database:', userData.role);
            console.log('🔍 Raw userData object:', JSON.stringify(userData, null, 2));

            // FORCE FIX: Always ensure masterfem@gmail.com is SUPER_ADMIN
            if (user.email === 'masterfem@gmail.com') {
              console.log('🔧 FORCE APPLYING SUPER_ADMIN role for masterfem@gmail.com');
              const userEmail = user.email!;
              const fixedUserData = {
                ...userData,
                email: userEmail,
                isAdmin: true,
                role: UserRole.SUPER_ADMIN,
                permissions: getRolePermissions(UserRole.SUPER_ADMIN),
                updatedAt: new Date()
              };
              console.log('🔧 Writing fixed user data to Firestore:', JSON.stringify(fixedUserData, null, 2));
              await setDoc(doc(db, 'usuariosAutorizados', userEmail), fixedUserData);
              console.log('🔧 Setting React state to SUPER_ADMIN');
              setCurrentUserRole(UserRole.SUPER_ADMIN);
              setUserPermissions(getRolePermissions(UserRole.SUPER_ADMIN));
              console.log('🔧 COMPLETED: masterfem@gmail.com role fix');
            } else {
              console.log('🔍 Setting role from database for non-masterfem user');
              setCurrentUserRole(userData.role);
              setUserPermissions(userData.permissions || getRolePermissions(userData.role));
            }

            // Log login
            try {
              await AuditLogger.logAction(
                user.uid,
                user.email || '',
                'LOGIN',
                'system',
                'login'
              );
            } catch (auditError) {
              console.error('Audit logging failed:', auditError);
              // Don't block the app if audit fails
            }
          } else {
            // User not in admin system - check if it's the allowed admin
            console.log('User not found in admin system, checking allowedAdmins');
            if (user.email && allowedAdmins.includes(user.email)) {
              console.log('✅ User is in allowedAdmins, granting SUPER_ADMIN');
              setCurrentUserRole(UserRole.SUPER_ADMIN);
              setUserPermissions(getRolePermissions(UserRole.SUPER_ADMIN));
            } else {
              console.log('User not authorized, treating as viewer');
              setCurrentUserRole(UserRole.VIEWER);
              setUserPermissions(getRolePermissions(UserRole.VIEWER));
            }
          }
        } catch (error) {
          console.error('❌ Database initialization failed:', error);
          clearTimeout(timeoutId);
          setInitializationError(`Initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          setCurrentUserRole(UserRole.VIEWER);
          setUserPermissions(getRolePermissions(UserRole.VIEWER));
          setMigrationComplete(true); // Set to true to prevent infinite loop
        }
      }
    };

    if (user && !migrationComplete) {
      initializeDatabase();
    }
  }, [user, migrationComplete]);

  return {
    user,
    loading,
    currentUserRole,
    userPermissions,
    migrationComplete,
    initializationError
  };
};